import React from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Tooltip,
    Typography,
    LinearProgress,
    Alert,
    Button,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
    Download as DownloadIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import moment from 'moment';
import apiClient from '../../api/client';

type ReportStatus = 'queued' | 'processing' | 'done' | 'failed';

interface ReportJob {
    id: string;
    type: string;
    params: { startDate?: string; endDate?: string };
    status: ReportStatus;
    file_size: number | null;
    error: string | null;
    created_at: string;
    started_at: string | null;
    finished_at: string | null;
}

const statusLabel: Record<ReportStatus, { label: string; color: 'default' | 'info' | 'success' | 'error' | 'warning' }> = {
    queued: { label: 'В очереди', color: 'default' },
    processing: { label: 'Генерируется', color: 'info' },
    done: { label: 'Готов', color: 'success' },
    failed: { label: 'Ошибка', color: 'error' },
};

const ReportsPage: React.FC = () => {
    const queryClient = useQueryClient();

    const { data, isLoading, error, refetch } = useQuery<ReportJob[]>(
        ['reports'],
        async () => {
            const res = await apiClient.get('/api/reports?limit=50');
            return res.data.data;
        },
        {
            refetchInterval: (data) => {
                const hasActive = (data || []).some(
                    (r) => r.status === 'queued' || r.status === 'processing'
                );
                return hasActive ? 3000 : false;
            },
            refetchOnWindowFocus: false,
        }
    );

    const deleteMutation = useMutation(
        async (id: string) => {
            await apiClient.delete(`/api/reports/${id}`);
        },
        { onSuccess: () => queryClient.invalidateQueries(['reports']) }
    );

    const handleDownload = async (job: ReportJob) => {
        try {
            const response = await apiClient.get(`/api/reports/${job.id}/download`, {
                responseType: 'blob',
                timeout: 60000,
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const name = `${job.type}_${job.params.startDate || ''}_${job.params.endDate || ''}.xlsx`;
            link.setAttribute('download', name);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            alert('Не удалось скачать отчёт');
        }
    };

    if (error) {
        return <Alert severity="error">Не удалось загрузить список отчётов</Alert>;
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="h5">Заказанные отчёты</Typography>
                <Button onClick={() => refetch()} startIcon={<RefreshIcon />} variant="outlined" size="small">
                    Обновить
                </Button>
            </Box>

            {isLoading && <LinearProgress sx={{ mb: 2 }} />}

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Тип</TableCell>
                            <TableCell>Период</TableCell>
                            <TableCell>Статус</TableCell>
                            <TableCell>Заказан</TableCell>
                            <TableCell>Готов</TableCell>
                            <TableCell>Размер</TableCell>
                            <TableCell align="right">Действия</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(data || []).length === 0 && !isLoading && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    <Typography color="text.secondary" sx={{ py: 4 }}>
                                        Пока нет заказанных отчётов. Закажите Excel-выгрузку KPI на странице «Аналитика».
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {(data || []).map((job) => {
                            const s = statusLabel[job.status];
                            const period =
                                job.params?.startDate && job.params?.endDate
                                    ? `${moment(job.params.startDate).format('DD.MM.YY')} – ${moment(job.params.endDate).format('DD.MM.YY')}`
                                    : '—';
                            return (
                                <TableRow key={job.id} hover>
                                    <TableCell>{job.type === 'kpi' ? 'KPI операторов' : job.type}</TableCell>
                                    <TableCell>{period}</TableCell>
                                    <TableCell>
                                        <Tooltip title={job.error || ''}>
                                            <Chip label={s.label} color={s.color} size="small" />
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>{moment(job.created_at).format('DD.MM.YY HH:mm:ss')}</TableCell>
                                    <TableCell>
                                        {job.finished_at ? moment(job.finished_at).format('DD.MM.YY HH:mm:ss') : '—'}
                                    </TableCell>
                                    <TableCell>
                                        {job.file_size ? `${Math.round(job.file_size / 1024)} КБ` : '—'}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Скачать">
                                            <span>
                                                <IconButton
                                                    onClick={() => handleDownload(job)}
                                                    disabled={job.status !== 'done'}
                                                    size="small"
                                                >
                                                    <DownloadIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Удалить">
                                            <IconButton
                                                onClick={() => deleteMutation.mutate(job.id)}
                                                size="small"
                                                color="error"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default ReportsPage;
